import type { ReactNode } from "react";

import { formatNumber } from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import { isABalance } from "byzantine/src/Transaction";
import { usesMxEnrichment } from "byzantine/src/TransactionEnrichmentSource";

import CategoryPanelRow from "./CategoryPanelRow/CategoryPanelRow";
import CheckPanelSection from "./CheckPanelSection/CheckPanelSection";
import NotesPanelSection from "./NotesPanelSection/NotesPanelSection";
import PanelCard from "./PanelCard/PanelCard";
import PanelRow from "./PanelRow/PanelRow";
import ReceiptPanelSection from "./ReceiptPanelSection";
import SubCategoryPanelRow from "./SubCategoryPanelRow/SubCategoryPanelRow";
import TagsPanelSection from "./TagsPanelSection/TagsPanelSection";

import type { TransactionPanelSection } from "./TransactionDetailsPanel";
import type { TransactionCategory } from "../hooks/useTransactionCategories";
import type { ReactLocalization } from "@fluent/react";
import type Account from "byzantine/src/Account";

type LedgerPanelSectionDependencies = {
  l10n: ReactLocalization;
  accounts: Account[];
  /** Picks the taxonomy for a row's own account, not the viewer's. */
  categoriesForTransaction: (
    transaction: API.Transaction
  ) => TransactionCategory[];
  canWrite: boolean;
  transactionEnrichmentEnabled: boolean;
  isMultiEntity: boolean;
  /**
   * The current user's own org name, from `currentUser.org_name`. The backend blanks
   * `Account.organization` for a user's own core identity, so this is the only name
   * available for the row on those accounts.
   */
  organizationFallbackName?: string;
  receipts?: {
    canWrite: boolean;
    uploadingTransactionIds: ReadonlySet<string>;
    acceptMimeTypes?: readonly string[];
    onFileChosen: (transaction: API.Transaction, file: File) => void;
    onRemove: (
      transaction: API.Transaction,
      options: { deleteReceipt: boolean }
    ) => Promise<void>;
  };
  /**
   * False once the panel closes. The notes autosave needs it, because closing does not unmount the
   * section and a failure then has to reach the user as a toast rather than an unseen inline state.
   */
  isPanelOpen: boolean;
  onDescriptionUpdate?: (description: string) => void;
};

type PanelField = {
  id: string;
  label: string;
  value: (transaction: API.Transaction) => ReactNode;
};

// Mirrors integrations.base.SpreadDetailsKeys, which is a closed set.
type SpreadKey =
  | "principal_amount"
  | "interest_amount"
  | "escrow"
  | "fees"
  | "unapplied_funds";

/**
 * Whether a loan breakdown row appears is decided by the core sending the key, not by
 * the account's type. An absent key yields an empty string and `PanelRow` drops a row
 * with empty content, so a card transaction renders none of these with no type check
 * anywhere. The values arrive already in dollars, hence `hasDecimal`.
 */
const spread = (transaction: API.Transaction, key: SpreadKey): string => {
  const value = transaction.metadata?.spread_details?.value?.[key];
  return value === undefined
    ? ""
    : Filters.currency(value, { hasDecimal: true });
};

const longDate = (value: string | null | undefined): string =>
  value ? Filters.longMonthDayYear(value) : "";

// AccountFilterPanel's getAccountLabel truncates the name to fit its 300px panel;
// the details panel has no such constraint, so the full name renders here instead.
const accountLabel = (
  transaction: API.Transaction,
  accounts: Account[]
): string => {
  const account = accounts.find(
    (candidate) => candidate.id === transaction.account_id
  );
  if (!account) return "";
  const name = account.getName();
  const maskedNumber = account.getMaskedNumber();
  return maskedNumber ? `${name}-${maskedNumber}` : name;
};

// Matches groupAccountsByOrganization's fallback: the backend blanks
// `organization` for the user's own core identity, so a matched account with
// no `organization` falls back to the user's own org name. An unmatched
// account gets no fallback, same as `accountLabel`.
const organizationLabel = (
  transaction: API.Transaction,
  accounts: Account[],
  fallbackName?: string
): string => {
  const account = accounts.find(
    (candidate) => candidate.id === transaction.account_id
  );
  if (!account) return "";
  return (
    account.organization?.name_dba ||
    account.organization?.name_legal ||
    fallbackName ||
    ""
  );
};

/**
 * The DETAILS rows above the category control, in Figma's order.
 *
 * The FTL ids are the sidebar's own `transaction-detail-*` keys rather than new
 * `live-ledger-*` ones. An FI suppresses a single field by setting its string to empty,
 * and reusing the ids carries that configuration over; new ids would silently drop it.
 */
const detailFields = (
  l10n: ReactLocalization,
  accounts: Account[],
  isMultiEntity: boolean,
  organizationFallbackName?: string
): PanelField[] => [
  {
    id: "posted-date",
    label: l10n.getString("transaction-detail-posted-date"),
    value: (transaction) =>
      transaction.settled_at
        ? longDate(transaction.settled_at)
        : l10n.getString("live-ledger-status-pending"),
  },
  {
    id: "account",
    label: l10n.getString("transaction-detail-account"),
    value: (transaction) => accountLabel(transaction, accounts),
  },
  ...(isMultiEntity
    ? [
        {
          id: "organization",
          label: l10n.getString("live-ledger-panel-organization-label"),
          value: (transaction: API.Transaction) =>
            organizationLabel(transaction, accounts, organizationFallbackName),
        },
      ]
    : []),
  {
    id: "transaction-type",
    label: l10n.getString("transaction-detail-transaction-type"),
    value: (transaction) => transaction.source,
  },
  {
    id: "resulting-balance",
    label: l10n.getString("transaction-detail-resulting-balance"),
    value: (transaction) => {
      if (!transaction.settled_at) {
        return l10n.getString("live-ledger-panel-resulting-balance-pending");
      }
      return isABalance(transaction.ledger_balance)
        ? formatNumber(Number(transaction.ledger_balance) / 100, "currency")
        : "";
    },
  },
  {
    id: "description",
    label: l10n.getString("transaction-detail-description"),
    value: (transaction) => transaction.raw_description,
  },
];

/**
 * Rows the sidebar shows that Figma's DETAILS section omits. They are kept because the
 * PRD's parity rule means a user must not see less detail here than in the per-account
 * sidebar, and they render below the category controls.
 */
const parityFields = (l10n: ReactLocalization): PanelField[] => [
  {
    id: "principal",
    label: l10n.getString("transaction-detail-principal"),
    value: (transaction) => spread(transaction, "principal_amount"),
  },
  {
    id: "interest",
    label: l10n.getString("transaction-detail-interest"),
    value: (transaction) => spread(transaction, "interest_amount"),
  },
  {
    id: "escrow",
    label: l10n.getString("transaction-detail-escrow"),
    value: (transaction) => spread(transaction, "escrow"),
  },
  {
    id: "fees",
    label: l10n.getString("transaction-detail-fees"),
    value: (transaction) => spread(transaction, "fees"),
  },
  {
    id: "unapplied",
    label: l10n.getString("transaction-detail-unapplied"),
    value: (transaction) => spread(transaction, "unapplied_funds"),
  },
];

const renderFields = (fields: PanelField[], transaction: API.Transaction) =>
  fields.map((field) => (
    <PanelRow
      key={field.id}
      label={field.label}
      testId={`panel-row-${field.id}`}
    >
      {field.value(transaction)}
    </PanelRow>
  ));

/**
 * The details panel's sections, in display order.
 *
 * Declared as data for the same reason the table's columns are: consumer banking
 * reuses this panel with a different set, so the list cannot be hardcoded in the tab.
 */
export const buildLedgerPanelSections = ({
  l10n,
  accounts,
  categoriesForTransaction,
  canWrite,
  transactionEnrichmentEnabled,
  isMultiEntity,
  organizationFallbackName,
  receipts,
  isPanelOpen,
  onDescriptionUpdate,
}: LedgerPanelSectionDependencies): TransactionPanelSection<API.Transaction>[] => {
  const sections: TransactionPanelSection<API.Transaction>[] = [
    {
      id: "details",
      title: l10n.getString("live-ledger-panel-details-title"),
      render: (transaction) => (
        <PanelCard testId="panel-details-card">
          {renderFields(
            detailFields(
              l10n,
              accounts,
              isMultiEntity,
              organizationFallbackName
            ),
            transaction
          )}
          <CategoryPanelRow
            transaction={transaction}
            categories={categoriesForTransaction(transaction)}
            canWrite={canWrite}
          />
          {(transactionEnrichmentEnabled || usesMxEnrichment(transaction)) && (
            <SubCategoryPanelRow
              transaction={transaction}
              categories={categoriesForTransaction(transaction)}
              canWrite={canWrite}
            />
          )}
          {renderFields(parityFields(l10n), transaction)}
        </PanelCard>
      ),
    },
  ];

  sections.push({
    id: "bai2",
    title: l10n.getString("live-ledger-panel-bai2-title"),
    // Null covers every API-resolved reason the section should not render: no code on the
    // row, an account outside the caller's organization, or an institution with no BAI2
    // mapping. Account type is a client-side addition on top of that: BAI2 is a business
    // reconciliation format and is meaningless on a consumer account.
    render: (transaction) =>
      transaction.bai2 && transaction.account_type === "business" ? (
        <PanelCard testId="panel-bai2-card">
          <PanelRow
            label={l10n.getString("live-ledger-panel-bai2-code-label")}
            testId="panel-row-bai2-code"
          >
            {transaction.bai2.code}
          </PanelRow>
          <PanelRow
            label={l10n.getString("live-ledger-panel-bai2-type-label")}
            testId="panel-row-bai2-type"
          >
            {transaction.bai2.type}
          </PanelRow>
        </PanelCard>
      ) : null,
  });

  // Absent entirely when the FI does not have receipts_enabled. No PanelCard
  // here: both of this section's states are already bordered in the design, so
  // wrapping it would double the border.
  if (receipts) {
    sections.push({
      id: "receipt",
      title: l10n.getString("live-ledger-panel-receipt-title"),
      render: (transaction) => (
        <ReceiptPanelSection
          transaction={transaction}
          canWrite={receipts.canWrite}
          isUploading={receipts.uploadingTransactionIds.has(transaction.id)}
          acceptMimeTypes={receipts.acceptMimeTypes}
          onFileChosen={receipts.onFileChosen}
          onRemove={receipts.onRemove}
        />
      ),
    });
  }

  sections.push({
    // No title: the check card renders its own collapsible header.
    id: "check",
    render: (transaction) => (
      <CheckPanelSection
        transaction={transaction}
        onDescriptionUpdate={onDescriptionUpdate}
      />
    ),
  });

  sections.push({
    // No title: the section draws its own heading so a pending row hides it along with the card.
    // Panel order is DETAILS, BAI2 DETAILS, RECEIPT, CHECK, TAGS, NOTES.
    id: "tags",
    render: (transaction) => <TagsPanelSection transaction={transaction} />,
  });

  sections.push({
    // No title: the section draws its own heading so the save status can sit on that line.
    id: "notes",
    render: (transaction) => (
      <NotesPanelSection transaction={transaction} isPanelOpen={isPanelOpen} />
    ),
  });

  return sections;
};
